import type { BillingCadence } from "@intelligems/ig-types/dtos/billing/commands/catalog";
import type { BillingPlanIntentDesiredPlanV1 } from "@intelligems/ig-types/dtos/billing/commands/plan-intent";
import type { BillingProductSetTarget } from "@intelligems/ig-types/dtos/billing/commands/payloads";
import type { StripeApiSubscriptionSchedulePhaseDto } from "@intelligems/ig-types/dtos/stripe-api/subscription";
import { StripeIntervalList } from "@intelligems/ig-types/dtos/subscriptions";
import { toBillingCadenceFromStripeInterval } from "@intelligems/ig-types/dtos/billing/commands/policy";

// Stripe speaks intervals; analytics speaks cadences. An interval the canonical
// converter cannot place — or one absent entirely — yields nothing, so the
// caller omits the property instead of reporting a quarterly plan as monthly.
export const toAnalyticsBillingCadence = (
  interval: string | null | undefined,
  intervalCount?: number | null
): BillingCadence | undefined => {
  const stripeInterval = StripeIntervalList.find((known) => known === interval);
  if (!stripeInterval) {
    return undefined;
  }

  return (
    toBillingCadenceFromStripeInterval(
      stripeInterval,
      intervalCount ?? undefined
    ) ?? undefined
  );
};

export const getDesiredProductSetTarget = (
  desiredPlan: BillingPlanIntentDesiredPlanV1
) => {
  const { intentPayload } = desiredPlan.normalizedIntent;
  return "requestedProductSetTarget" in intentPayload
    ? intentPayload.requestedProductSetTarget
    : undefined;
};

export const getProductSetTargetFromPhase = (
  phase: StripeApiSubscriptionSchedulePhaseDto | undefined
): BillingProductSetTarget | undefined => {
  const activeItems = phase?.items.filter((item) => !item.deleted) ?? [];
  const version = activeItems[0]?.product.version;
  const priceIds = activeItems.flatMap((item) =>
    item.price?.id ? [item.price.id] : []
  );

  if (!version || priceIds.length !== activeItems.length) {
    return undefined;
  }

  return {
    version,
    priceIds,
    manualCouponIds: (phase?.operatorCoupons ?? []).map((coupon) => coupon.id),
  };
};

export const getPhaseBillingCadence = (
  phase: StripeApiSubscriptionSchedulePhaseDto
): BillingCadence => {
  const activeItem = phase.items.find((item) => !item.deleted);
  const interval = activeItem?.price?.interval;
  const intervalCount = activeItem?.price?.intervalCount ?? 1;

  return (
    (interval
      ? toBillingCadenceFromStripeInterval(interval, intervalCount)
      : null) ?? "monthly"
  );
};

export const getDesiredBillingCadence = (
  desiredPlan: BillingPlanIntentDesiredPlanV1,
  desiredPriceIdentities: Array<{
    interval: "month" | "year" | null;
  }> = []
): BillingCadence | undefined => {
  const { intentPayload } = desiredPlan.normalizedIntent;
  if ("requestedCadence" in intentPayload && intentPayload.requestedCadence) {
    return intentPayload.requestedCadence;
  }

  const intervals = new Set(
    desiredPriceIdentities.flatMap(({ interval }) =>
      interval === null ? [] : [interval]
    )
  );
  if (intervals.size !== 1) {
    return undefined;
  }

  return intervals.has("year") ? "annual" : "monthly";
};

const applyIdDelta = (
  desiredIds: string[],
  appliedIds: string[],
  finalIds: string[]
): string[] => [
  ...desiredIds.filter(
    (id) => !appliedIds.includes(id) || finalIds.includes(id)
  ),
  ...finalIds.filter(
    (id) => !appliedIds.includes(id) && !desiredIds.includes(id)
  ),
];

export const applyDesiredProductSetEdits = (args: {
  desiredTarget: BillingProductSetTarget;
  appliedTarget: BillingProductSetTarget;
  finalPhase: StripeApiSubscriptionSchedulePhaseDto;
  productIdByPriceId: Record<string, string>;
  editedProductIds?: string[];
  couponsEdited?: boolean;
}): BillingProductSetTarget => {
  const { desiredTarget, appliedTarget, finalPhase } = args;
  const finalTarget = getProductSetTargetFromPhase(finalPhase);

  if (!finalTarget) {
    return desiredTarget;
  }

  const productIdByPriceId = {
    ...args.productIdByPriceId,
    ...Object.fromEntries(
      finalPhase.items.flatMap((item) =>
        item.price?.id ? [[item.price.id, item.product.id]] : []
      )
    ),
  };
  const groupByProduct = (priceIds: string[]) =>
    priceIds.reduce<Record<string, string[]>>((groups, priceId) => {
      const productId = productIdByPriceId[priceId];
      if (productId) {
        (groups[productId] ??= []).push(priceId);
      }
      return groups;
    }, {});
  const appliedByProduct = groupByProduct(appliedTarget.priceIds);
  const finalByProduct = groupByProduct(finalTarget.priceIds);
  const unmappedDesiredPriceIds = desiredTarget.priceIds.filter(
    (priceId) => !productIdByPriceId[priceId]
  );
  const changedProductIds = new Set([
    ...Object.keys(appliedByProduct),
    ...Object.keys(finalByProduct),
    ...(args.editedProductIds ?? []),
  ]);
  const explicitlyEditedProductIds = new Set(args.editedProductIds ?? []);
  let priceIds = [...desiredTarget.priceIds];

  for (const productId of changedProductIds) {
    const appliedPriceIds = appliedByProduct[productId] ?? [];
    const finalPriceIds = finalByProduct[productId] ?? [];

    if (
      !explicitlyEditedProductIds.has(productId) &&
      appliedPriceIds.length === finalPriceIds.length &&
      appliedPriceIds.every((priceId) => finalPriceIds.includes(priceId))
    ) {
      continue;
    }

    if (unmappedDesiredPriceIds.length > 0) {
      throw new Error(
        "Cannot safely merge the pending desired plan because one or more desired prices have no product identity"
      );
    }

    priceIds = [
      ...priceIds.filter(
        (priceId) => productIdByPriceId[priceId] !== productId
      ),
      ...finalPriceIds,
    ];
  }

  // The drawer's coupon list is hydrated from the live subscription, so a
  // difference from the applied list is not evidence of an edit: only the
  // explicit edit flag states the list. An untouched save with no pending
  // desired list omits it, which preserves the live Operator Coupons.
  // An edit states the drawer's list outright and supersedes a pending desired
  // list: the drawer never shows pending coupons, so merging one back in would
  // re-add a coupon the operator cannot see or remove.
  const desiredCouponIds = desiredTarget.manualCouponIds;
  const appliedCouponIds = appliedTarget.manualCouponIds ?? [];
  const finalCouponIds = finalTarget.manualCouponIds ?? [];
  const manualCouponIds = args.couponsEdited
    ? finalCouponIds
    : desiredCouponIds === undefined
      ? undefined
      : applyIdDelta(desiredCouponIds, appliedCouponIds, finalCouponIds);
  const { manualCouponIds: _desiredCouponIds, ...desiredTargetBase } =
    desiredTarget;

  return {
    ...desiredTargetBase,
    priceIds,
    ...(manualCouponIds === undefined ? {} : { manualCouponIds }),
  };
};
